<!-- 父组件 HomeView.vue -->
<script setup>
import { onMounted, ref, computed, nextTick, onUnmounted } from 'vue'
import { showToast } from 'vant'
// 导入企业微信工具函数
import {
  autoInitWecom,
  getCurrentExternalContact,
  checkRegistered,
  getCurExternalChat,
  getWecomContext,
  isInCustomerScene,
  getEntrySceneDesc,
} from '@/utils/wecom'
import { WecomEntryScene } from '@/config/wecomConfig'
// 导入AI对话接口
import { sendChatMessageStream } from '@/api/chat'
import { getCurrentUser } from '@/api/user'
import { recordUserEnter } from '@/api/event'
import ContentArea from '@/components/ContentArea.vue'
import HomeContent from '@/components/HomeContent.vue'
import indexedDB from '@/utils/indexedDB'

let originalPushState = window.history.pushState
let textareaBlurHandler = null
let handleResize = null // 提前声明resize处理函数
let handleTextareaFocus = null // 提前声明输入框聚焦处理函数

// 定义popstate处理函数
const handlePopState = (e) => {
  e.preventDefault()
  window.history.replaceState(null, document.title, window.location.href)
}

// 状态管理
const registerStatus = ref('未开始注册')
const registerResult = ref(null)
/* 删除未使用的变量以消除lint告警 */
// const externalContact = ref(null)
// const messageContent = ref('测试消息：Hello World！')
// 对话信息状态
const conversationMessages = ref([])
// 加载状态
const isLoading = ref(false)
const isLoadingHistory = ref(true)
// 场景相关状态
const wecomContext = ref(null) // 存储场景信息
const currentSceneDesc = ref('') // 场景中文描述
const supportCustomerOperate = ref(false) // 是否支持客户相关操作
// 新增：记录当前存储的ID类型（contact/chat/none）
const currentIdType = ref('none') // contact:联系人ID, chat:群ID, none:无

// 输入框和筛选条件状态
const userQuery = ref('')
const isFilterExpanded = ref(false)
const filterExpanded = ref({})

// 筛选条件状态
const filterAge = ref('')
const filterGender = ref('')
const filterWeight = ref('')
const filterFeeding = ref('')
const filterItems = ref([
  { label: '宝宝年龄', value: filterAge, options: ['0-6个月', '6-12个月', '1-2岁', '2-3岁'] },
  { label: '宝宝性别', value: filterGender, options: ['男', '女'] },
  { label: '宝宝体重', value: filterWeight, options: ['0-6kg', '6-12kg', '12-18kg'] },
  { label: '喂养方式', value: filterFeeding, options: ['纯母乳', '混合喂养', '配方奶'] },
])
const isKeyboardAdjusting = ref(false)
const homeViewRef = ref(null)
let visualViewportResizeHandler = null

onUnmounted(() => {
  // 还原原始pushState
  window.history.pushState = originalPushState
  // 移除事件监听（判断函数存在，避免报错）
  window.removeEventListener('popstate', handlePopState)
  if (handleResize) {
    window.removeEventListener('resize', handleResize)
  }
  const textarea = document.querySelector('.query-input')
  if (textarea && textareaBlurHandler) {
    textarea.removeEventListener('blur', textareaBlurHandler)
  }
  if (textarea && handleTextareaFocus) {
    textarea.removeEventListener('focus', handleTextareaFocus)
  }
  if (window.visualViewport && visualViewportResizeHandler) {
    window.visualViewport.removeEventListener('resize', visualViewportResizeHandler)
    visualViewportResizeHandler = null
  }
  resetLayoutAfterKeyboard()
})
// 计算属性：判断是否有对话信息
const hasConversation = computed(() => {
  // 确保在加载历史结束后，并且conversationMessages是数组且长度大于0时才返回true
  return (
    !isLoadingHistory.value &&
    Array.isArray(conversationMessages.value) &&
    conversationMessages.value.length > 0
  )
})

// 优化输入框聚焦滚动逻辑，适配企业微信键盘弹出
const adjustLayoutForKeyboard = () => {
  const homeViewEl = homeViewRef.value || document.querySelector('.home-view')
  if (!homeViewEl) return
  const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight
  if (viewportHeight) {
    homeViewEl.style.height = `${viewportHeight}px`
  }
  if (window.visualViewport && window.innerHeight) {
    const keyboardHeight = Math.max(window.innerHeight - window.visualViewport.height, 0)
    homeViewEl.style.paddingBottom = keyboardHeight > 0 ? `${keyboardHeight}px` : ''
  }
}

const resetLayoutAfterKeyboard = () => {
  const homeViewEl = homeViewRef.value || document.querySelector('.home-view')
  if (!homeViewEl) return
  homeViewEl.style.height = ''
  homeViewEl.style.paddingBottom = ''
}

const ensureInputVisible = () => {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const textarea = document.querySelector('.query-input')
        if (textarea) {
          const container = document.querySelector('.content-wrapper')
          if (container) {
            container.scrollTop = container.scrollHeight
          }
          textarea.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
        resolve()
      })
    })
  })
}

handleTextareaFocus = () => {
  isKeyboardAdjusting.value = true
  adjustLayoutForKeyboard()
  if (window.visualViewport && !visualViewportResizeHandler) {
    visualViewportResizeHandler = () => {
      if (isKeyboardAdjusting.value) {
        adjustLayoutForKeyboard()
        ensureInputVisible()
      }
    }
    window.visualViewport.addEventListener('resize', visualViewportResizeHandler)
  }
  ensureInputVisible()
}
// 企业微信初始化
const initWecom = async () => {
  try {
    registerStatus.value = '正在获取签名并注册...'
    const result = await autoInitWecom([
      'getCurExternalContact',
      'sendChatMessage',
      'getCurExternalChat',
      'getContext',
    ])
    registerResult.value = result
    registerStatus.value = '注册成功'

    // 注册成功后获取场景信息
    await getSceneInfo()
    return true
  } catch (error) {
    registerResult.value = { errMsg: error.message }
    registerStatus.value = '注册失败'
    showToast(`企业微信初始化失败：${error.message || '请检查网络'}`)
    return false
  }
}

// 获取企业微信场景信息
const getSceneInfo = async () => {
  try {
    if (!checkRegistered()) {
      throw new Error('企业微信未注册')
    }
    const context = await getWecomContext()
    wecomContext.value = context
    currentSceneDesc.value = getEntrySceneDesc(context.entry)

    // 判断是否支持客户相关操作
    const support = await isInCustomerScene()
    supportCustomerOperate.value = support

    // 非客户场景给出友好提示
    if (!support) {
      showToast(`当前场景：${currentSceneDesc.value}，部分客户相关功能可能受限`, { duration: 2000 })
    }
  } catch (error) {
    console.error('获取场景信息失败：', error)
    currentSceneDesc.value = '未知场景'
    supportCustomerOperate.value = false
    if (error.message.includes('版本过低')) {
      showToast('当前企业微信版本过低，部分功能可能无法使用', { duration: 2000 })
    }
  }
}

// 新增：根据场景判断应该调用哪个获取ID的方法
const getTargetIdByScene = async () => {
  if (!wecomContext.value) {
    console.warn('未获取到场景信息，无法确定目标ID类型')
    return null
  }

  const { entry } = wecomContext.value

  // 联系人相关场景：调用获取联系人ID的方法
  const contactScenes = [
    WecomEntryScene.CONTACT_PROFILE,
    WecomEntryScene.SINGLE_CHAT_TOOLS,
    WecomEntryScene.SINGLE_KF_TOOLS,
    WecomEntryScene.CHAIN_SINGLE_CHAT_TOOLS,
  ]

  // 群聊相关场景：调用获取客户群ID的方法
  const groupScenes = [
    WecomEntryScene.GROUP_CHAT_TOOLS,
    WecomEntryScene.CHAIN_GROUP_CHAT_TOOLS,
    WecomEntryScene.INTERNAL_GROUP_CHAT_BOARD,
    WecomEntryScene.CHAT_ATTACHMENT, // 聊天附件栏可能是群聊或单聊，但优先按群聊处理
  ]

  try {
    let targetId = null // 定义局部变量targetId
    if (contactScenes.includes(entry)) {
      // 联系人场景：获取联系人ID
      const contactResult = await getCurrentExternalContact()
      targetId = contactResult.userId
      currentIdType.value = 'contact'
    } else if (groupScenes.includes(entry)) {
      // 群聊场景：获取客户群ID
      const chatResult = await getCurExternalChat()
      targetId = chatResult.chatId
      currentIdType.value = 'chat'
    }
    return targetId
  } catch {
    currentIdType.value = 'none'
    return null
  }
}

/** 是否存在仍在生成中的消息（流式输出中） */
const hasGeneratingMessage = (messages) =>
  Array.isArray(messages) &&
  messages.some(
    (m) =>
      m.isGenerating === true ||
      (Array.isArray(m.style) && m.style.some((s) => s.isGenerating === true)),
  )

/** 原子更新某条消息的指定 style 流式内容，避免详细版/简化版并行回调互相覆盖 */
const patchStreamingStyleContent = (messageIndex, styleNum, processedContent) => {
  if (messageIndex < 0 || messageIndex >= conversationMessages.value.length) return
  conversationMessages.value = conversationMessages.value.map((msg, idx) => {
    if (idx !== messageIndex || !Array.isArray(msg.style)) return msg
    return {
      ...msg,
      isGenerating: true,
      style: msg.style.map((item) =>
        Number(item.style) === Number(styleNum)
          ? { ...item, content: processedContent, isGenerating: true }
          : item,
      ),
    }
  })
  updateConversationMessages(conversationMessages.value)
}

let streamFollowRafId = null
let lastStreamFollowScrollAt = 0
/** 流式跟随时两次调整 scrollTop 的最小间隔，减轻高频重排导致的跳动 */
const STREAM_FOLLOW_SCROLL_MIN_MS = 72
/** 距底部小于该值则视为「贴在底部」，才自动跟随流式增长 */
const STREAM_STICKY_BOTTOM_PX = 140

/**
 * 流式输出时跟到底部：节流 + 仅当用户未大幅上滑时才滚动，避免整页剧烈抖动。
 */
const scheduleFollowStreamScroll = () => {
  if (isKeyboardAdjusting.value) return
  if (streamFollowRafId != null) return
  streamFollowRafId = requestAnimationFrame(() => {
    streamFollowRafId = null
    const now = performance.now()
    if (now - lastStreamFollowScrollAt < STREAM_FOLLOW_SCROLL_MIN_MS) {
      const delay = STREAM_FOLLOW_SCROLL_MIN_MS - (now - lastStreamFollowScrollAt)
      setTimeout(() => scheduleFollowStreamScroll(), delay)
      return
    }
    const container = document.querySelector('.content-wrapper')
    if (!container) return
    const gap = container.scrollHeight - container.scrollTop - container.clientHeight
    if (gap > STREAM_STICKY_BOTTOM_PX) return
    container.scrollTop = container.scrollHeight
    lastStreamFollowScrollAt = performance.now()
  })
}

// 更新对话信息的方法
const updateConversationMessages = async (messages) => {
  if (messages && Array.isArray(messages) && messages.length > 0) {
    conversationMessages.value = messages
    await nextTick()
    if (hasGeneratingMessage(messages)) {
      scheduleFollowStreamScroll()
    } else {
      scrollToBottom()
    }
    if (isKeyboardAdjusting.value) {
      ensureInputVisible()
    }
  }
}

// 滚动到底部的方法
const scrollToBottom = () => {
  if (isKeyboardAdjusting.value) return
  nextTick(() => {
    const container = document.querySelector('.content-wrapper')
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  })
}

// 筛选条件处理方法
const handleButton = (index) => {
  const currentState = filterExpanded.value[index]
  Object.keys(filterExpanded.value).forEach((key) => {
    filterExpanded.value[key] = false
  })
  filterExpanded.value[index] = !currentState
  isFilterExpanded.value = Object.values(filterExpanded.value).some((v) => v)
}

const selectFilterOption = (index, option) => {
  filterItems.value[index].value = option
  const filterLabel = filterItems.value[index].label
  userQuery.value = `${userQuery.value ? userQuery.value + '，' : ''}${filterLabel}：${option}`
  nextTick(() => {
    const textarea = document.querySelector('.query-input')
    if (textarea) {
      textarea.style.height = 'auto'
      const newHeight = Math.min(textarea.scrollHeight, 100)
      textarea.style.height = newHeight + 'px'
    }
  })
  filterExpanded.value[index] = false
  isFilterExpanded.value = false
}

// 输入框自适应
const autoResizeTextarea = (event) => {
  const textarea = event.target || document.querySelector('.query-input')
  if (!textarea) return

  const scrollTop = document.querySelector('.content-wrapper')?.scrollTop
  textarea.style.height = 'auto'
  const newHeight = Math.min(textarea.scrollHeight, 100)
  textarea.style.height = newHeight + 'px'
  if (scrollTop !== undefined) {
    document.querySelector('.content-wrapper').scrollTop = scrollTop
  }
}

// 生成唯一UUID的函数
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// 发送咨询
const sendQuery = async () => {
  const query = userQuery.value.trim()
  if (!query) {
    showToast('请输入要咨询问题')
    return
  }

  // 创建新的用户消息
  const userMessage = {
    type: 'user',
    content: query,
    timestamp: Date.now(),
  }
  conversationMessages.value = [...conversationMessages.value, userMessage]
  updateConversationMessages(conversationMessages.value)

  // 清空输入框
  userQuery.value = ''
  nextTick(() => {
    const textarea = document.querySelector('.query-input')
    if (textarea) {
      textarea.style.height = 'auto'
    }
  })

  // 显示加载状态
  isLoading.value = true

  try {
    // 根据场景获取对应的ID
    let userIdentifier = localStorage.getItem('wecom_external_userid')

    // 如果还没有ID，尝试根据场景获取
    if (!userIdentifier) {
      userIdentifier = await getTargetIdByScene()
    }

    // 验证ID是否存在
    if (!userIdentifier) {
      userIdentifier = 'unknown_user'
      //throw new Error('无法获取有效的用户ID或群ID')
    }

    //生成唯一的uuid
    const uuid = generateUUID()

    const localDefaultStyle = Number(localStorage.getItem('default_style') || 1) === 2 ? 2 : 1

    // 调用接口两次 分别传入style=1和2
    // 创建AI消息对象
    const aiResponse = {
      type: 'assistant',
      content: '',
      timestamp: Date.now(),
      isGenerating: true,
      vote: 0,
      advice: null,
      hasVoted: false,
      voteType: 0,
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      default_style: localDefaultStyle,
      // style:1
      style: [
        { style: 1, content: '', isGenerating: true },
        { style: 2, content: '', isGenerating: true }
      ]
    }
    const aiMessageIndex = conversationMessages.value.length
      conversationMessages.value = [...conversationMessages.value, aiResponse]
      updateConversationMessages(conversationMessages.value)

     // 同时调用两个接口，并行执行
    const promise1 = sendChatMessageStream(
      query.trim(),
      userIdentifier,
      (chunkContent) => {
        const processedContent =
          typeof chunkContent === 'string' ? chunkContent : String(chunkContent)
        patchStreamingStyleContent(aiMessageIndex, 1, processedContent)
      },
      null, // record_id
      -1, // messageIndex
      1, // style=1（详细版）
      uuid, // uuid参数
      localDefaultStyle
    )
    const promise2 = sendChatMessageStream(
      query.trim(),
      userIdentifier,
      (chunkContent) => {
        const processedContent =
          typeof chunkContent === 'string' ? chunkContent : String(chunkContent)
        patchStreamingStyleContent(aiMessageIndex, 2, processedContent)
      },
      null, // record_id
      -1, // messageIndex
      2, // style=2（简化版）
      uuid, // uuid参数
      localDefaultStyle
    )

    // 等待两个请求都完成
    await Promise.all([promise1, promise2])

    // 标记生成结束
    conversationMessages.value[aiMessageIndex].isGenerating = false
     // 更新style数组中的isGenerating状态
    conversationMessages.value[aiMessageIndex].style = conversationMessages.value[aiMessageIndex].style.map(item =>
      ({ ...item, isGenerating: false })
    )
    // 顶层content与当前默认展示风格保持一致，避免版本内容串位
    const finalStyleItem = conversationMessages.value[aiMessageIndex].style.find(
      (item) => Number(item.style) === Number(localDefaultStyle),
    )
    conversationMessages.value[aiMessageIndex].content = finalStyleItem?.content || ''
    updateConversationMessages(conversationMessages.value)
  } catch (error) {
    console.error('获取AI回复失败:', error)
    conversationMessages.value = [
      ...conversationMessages.value,
      {
        type: 'assistant',
        content: `获取回复失败: ${error.message || '请稍后重试'}`,
        isGenerating: false,
      },
    ]
    updateConversationMessages(conversationMessages.value)
  } finally {
    isLoading.value = false
  }
}

// 处理重新生成回复
const handleRefreshReply = async (data) => {
  const { index, record_id,style = 1 } = data

  if (index === undefined || index < 0 || index >= conversationMessages.value.length) {
    console.error('无效的消息索引')
    return
  }

  const message = conversationMessages.value[index]

  conversationMessages.value[index].content = ''
  conversationMessages.value[index].isGenerating = true
  // 同时更新style数组中对应版本的状态
  if (conversationMessages.value[index].style && Array.isArray(conversationMessages.value[index].style)) {
    conversationMessages.value[index].style = conversationMessages.value[index].style.map(styleItem => {
      if (Number(styleItem.style) === Number(style)) {
        return {
          ...styleItem,
          content: '',
          isGenerating: true,
          vote: 0,
          advice: null,
          hasVoted: false,
          voteType: 0,
        }
      }
      return styleItem
    })
    // 重置顶层投票状态为当前style的状态
    const currentStyleContent = conversationMessages.value[index].style.find(
      (item) => Number(item.style) === Number(style)
    )
    if (currentStyleContent) {
      conversationMessages.value[index].voteType = currentStyleContent.voteType || 0
      conversationMessages.value[index].advice = currentStyleContent.advice || null
      conversationMessages.value[index].hasVoted = currentStyleContent.hasVoted || false
    }
  } else {
    // 如果没有style数组，只重置顶层状态
    conversationMessages.value[index].voteType = 0
    conversationMessages.value[index].advice = null
    conversationMessages.value[index].hasVoted = false
  }
  updateConversationMessages(conversationMessages.value)

  isLoading.value = true

  try {
    // 根据场景获取对应的ID
    let userIdentifier = localStorage.getItem('wecom_external_userid')

    if (!userIdentifier) {
      userIdentifier = await getTargetIdByScene()
    }

    if (!userIdentifier) {
      userIdentifier = 'unknown_user'
      //throw new Error('未找到有效的用户ID或群ID')
    }

    const result = await sendChatMessageStream(
      '',
      userIdentifier,
      (chunkContent) => {
        const processedContent =
          typeof chunkContent === 'string' ? chunkContent : String(chunkContent)
        conversationMessages.value[index] = {
          ...conversationMessages.value[index],
          isGenerating: true,
          default_style: Number(style) === 2 ? 2 : 1,
          uuid:message.uuid
        }
        // 同时更新style数组中对应版本的内容和状态
        if (conversationMessages.value[index].style && Array.isArray(conversationMessages.value[index].style)) {
          conversationMessages.value[index].style = conversationMessages.value[index].style.map(styleItem => {
            if (Number(styleItem.style) === Number(style)) {
              return {
                ...styleItem,
                content: processedContent,
                isGenerating: true
              }
            }
            return styleItem
          })
        }
        conversationMessages.value = [...conversationMessages.value]
        updateConversationMessages(conversationMessages.value)
      },
      record_id,
      // userIdentifier,
      -1,
      style
    )

    //标记生成结束
    conversationMessages.value[index].isGenerating = false
    //更新对应style内容和record_id
    if(conversationMessages.value[index].style &&
    Array.isArray (conversationMessages.value[index].style)){
      conversationMessages.value[index].style = conversationMessages.value[index].style.map(styleItem => {
        if (Number(styleItem.style) === Number(style)) {
          return {
            ...styleItem,
            content: result.content,
            record_id: result.record_id,
            isGenerating: false
          }
        }
        return styleItem
      })
    }
    // 更新消息的 record_id
    conversationMessages.value[index].record_id = result.record_id;
    updateConversationMessages(conversationMessages.value)
    if (isKeyboardAdjusting.value) {
      ensureInputVisible()
    }
  } catch (error) {
    console.error('重新生成回复失败:', error)
    conversationMessages.value[index].content = `获取回复失败: ${error.message || '请稍后重试'}`
    conversationMessages.value[index].isGenerating = false
    // 同时更新style数组中对应版本的状态
    if (conversationMessages.value[index].style && Array.isArray(conversationMessages.value[index].style)) {
      conversationMessages.value[index].style = conversationMessages.value[index].style.map(styleItem => {
        if (Number(styleItem.style) === Number(style)) {
          return {
            ...styleItem,
            content: `获取回复失败: ${error.message || '请稍后重试'}`,
            isGenerating: false
          }
        }
        return styleItem
      })
    }
    updateConversationMessages(conversationMessages.value)
  } finally {
    isLoading.value = false
  }
}

// 处理直接发送问题
const handleSendQuestion = async (question) => {
  const currentQuery = userQuery.value

  try {
    userQuery.value = question
    await sendQuery()
  } catch (error) {
    console.error('发送问题失败:', error)
    userQuery.value = currentQuery
    isLoading.value = false
  }
}

onMounted(async () => {
  // 隐藏底部工具栏
  setTimeout(() => {
    const hideWecomToolbar = () => {
      const bridge = window.WeixinJSBridge || window.WxJSBridge
      if (bridge) {
        bridge.call('hideToolbar')
        bridge.call('hideOptionMenu')
      } else {
        document.addEventListener(
          'WeixinJSBridgeReady',
          () => {
            const bridge = window.WeixinJSBridge || window.WxJSBridge
            bridge.call('hideToolbar')
            bridge.call('hideOptionMenu')
          },
          false,
        )
      }
    }
    hideWecomToolbar()
  }, 300)

  // 强制清空所有历史记录
  const clearHistory = () => {
    const currentUrl = window.location.href
    window.history.forward()
    window.history.replaceState(null, document.title, currentUrl)
    let count = 0
    const interval = setInterval(() => {
      if (window.history.length > 1 && count < 5) {
        window.history.replaceState(null, document.title, currentUrl)
        count++
      } else {
        clearInterval(interval)
      }
    }, 100)
  }

  setTimeout(clearHistory, 300)

  // 拦截popstate事件
  window.addEventListener('popstate', handlePopState, { passive: false })

  // 重写pushState（不保留本地变量，避免未使用告警）
  window.history.pushState = function (...args) {
    return window.history.replaceState(...args)
  }

  const isWecomReady = await initWecom()
  if (isWecomReady) {
    // 新增：根据场景自动获取对应的ID，不再同时调用两个方法
    const userIdentifier = await getTargetIdByScene()

    // 使用获取到的ID进行用户记录
    await getCurrentUser()
    if (userIdentifier) {
      await recordUserEnter(userIdentifier)
    }
    //加载历史对话
    try {
      isLoadingHistory.value = true
      if (userIdentifier) {
        const historyRecords = await indexedDB.getUserChatRecords(userIdentifier)
        // 检查historyRecords是否存在且为数组，并且数组长度大于0
        if (historyRecords && Array.isArray(historyRecords) && historyRecords.length > 0) {
          const messages = []
          historyRecords.forEach((record) => {
            messages.push({
              type: 'user',
              content: record.query,
              timestamp: record.timestamp,
            })
            //查找style=1的内容 作为默认显示内容
            let defaultContent = record.content
            let defaultVote = record.vote
            let defaultAdvice = record.advice
            let defaultHasVoted = record.hasVoted || false
            let defaultVoteType = record.voteType || record.vote || 0
            if (record.style && Array.isArray(record.style)) {
              const style1Content = record.style.find(item => item.style === 1)
              if (style1Content && style1Content.content) {
                defaultContent = style1Content.content
                // 使用style1的投票状态
                defaultVote = style1Content.vote
                defaultAdvice = style1Content.advice
                defaultHasVoted = style1Content.hasVoted || false
                defaultVoteType = style1Content.voteType || style1Content.vote || 0
              }
            }
            messages.push({
              type: 'assistant',
              content: defaultContent,
              timestamp: record.timestamp,
              isGenerating: false,
              vote: defaultVote,
              advice: defaultAdvice,
              hasVoted: defaultHasVoted,
              voteType: defaultVoteType,
              style: record.style, // 传递style数组
              record_id: record.record_id, // 传递record_id
              uuid: record.uuid, // 传递uuid
            })
          })

          conversationMessages.value = messages
          // 无论messages长度如何，都更新conversationMessages
          updateConversationMessages(conversationMessages.value)
        }
      }
    } catch (error) {
      console.error('从IndexedDB加载历史对话失败:', error)
    } finally {
      isLoadingHistory.value = false
    }
  }

  filterItems.value.forEach((_, index) => {
    filterExpanded.value[index] = false
  })

  handleResize = () => {
    if (window.innerHeight === document.documentElement.clientHeight) {
      window.scrollTo(0, 0)
      nextTick(() => {
        const container = document.querySelector('.home-view')
        if (container) {
          container.style.height = '100vh'
          container.offsetHeight
          container.style.height = '100vh'
        }
      })
    }
  }

  window.addEventListener('resize', handleResize)

  const textarea = document.querySelector('.query-input')
  if (textarea) {
    // 给顶层textareaBlurHandler赋值，用于onUnmounted解绑
    textareaBlurHandler = () => {
      isKeyboardAdjusting.value = false
      resetLayoutAfterKeyboard()
      if (window.visualViewport && visualViewportResizeHandler) {
        window.visualViewport.removeEventListener('resize', visualViewportResizeHandler)
        visualViewportResizeHandler = null
      }
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 300)
    }
    textarea.addEventListener('blur', textareaBlurHandler)
    textarea.addEventListener('focus', handleTextareaFocus)
  }
})
</script>

<template>
  <div ref="homeViewRef" class="home-view" :class="{ 'has-content': hasConversation || isLoading }">
    <!-- 内容区域 -->
    <div class="content-wrapper">
      <div v-if="isLoadingHistory" class="history-loading">
        <div class="loading-spinner"></div>
      </div>
      <!-- ContentArea组件只在有对话或加载中时显示 -->
      <ContentArea
        v-if="hasConversation || isLoading"
        :initial-messages="conversationMessages"
        :is-loading="isLoading"
        @update-messages="updateConversationMessages"
        @refresh-reply="handleRefreshReply"
      />
      <!-- HomeContent只在没有对话且未加载历史时显示，避免加载期间闪现默认问题 -->
      <HomeContent
        v-else-if="!isLoadingHistory"
        @update-messages="updateConversationMessages"
        @send-question="handleSendQuestion"
      />
    </div>

    <!-- 底部区域 -->
    <div class="bottom-area" v-show="!isLoadingHistory">
      <!-- 底部输入区域 -->
      <div class="input-area">
        <div class="input-wrapper">
          <textarea
            v-model="userQuery"
            placeholder="请在此处输入或粘贴用户的咨询问题"
            class="query-input"
            rows="1"
            @input="autoResizeTextarea"
            @keydown.enter.prevent="sendQuery"
          ></textarea>
          <button class="query-send-btn" @click="sendQuery" :disabled="isLoading">
            <img v-if="isLoading" src="@/assets/loading.png" alt="" />
            <img v-else src="@/assets/send.png" alt="" />
          </button>
        </div>
      </div>
      <!-- 筛选条件 -->
      <div class="filter-bar">
        <div class="filter-content">
          <!-- 筛选标题栏 -->
          <div class="filter-headers-container">
            <div v-for="(item, index) in filterItems" :key="index" class="filter-header-item">
              <div
                class="filter-header"
                :class="{ active: filterExpanded[index] }"
                @touchstart.prevent.stop="handleButton(index)"
                @click="handleButton(index)"
              >
                <span>{{ item.label }}</span>
                <img
                  v-if="filterExpanded[index]"
                  src="@/assets/icon-back.png"
                  alt="收起"
                  class="filter-icon"
                />
                <img v-else src="@/assets/iconBack.png" alt="展开" class="filter-icon" />
              </div>
            </div>
          </div>

          <!-- 筛选选项容器 -->
          <div
            class="filter-options-container"
            :class="{ show: Object.values(filterExpanded).some((v) => v) }"
          >
            <div v-for="(item, index) in filterItems" :key="index">
              <div class="filter-options-content" v-if="filterExpanded[index]">
                <button
                  v-for="option in item.options"
                  :key="option"
                  class="option-btn"
                  :class="{ active: item.value === option }"
                  @touchstart.prevent.stop="selectFilterOption(index, option)"
                  @click="selectFilterOption(index, option)"
                >
                  {{ option }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
html,
body {
  overflow: hidden;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
}
</style>

<style scoped>
/* 主容器样式 */
.home-view {
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  touch-action: none; /* 禁用触摸手势的默认行为 */
  background-color: #f4f5fc;
  background-image: url('@/assets/image.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed; /* 可选：固定背景图不随滚动移动 */
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  position: relative;
}

.home-view.has-content {
  background-image: none;
  background-color: #f4f5fc;
}

/* 禁用组件切换时的默认行为 */
.content-wrapper > * {
  transition: none !important;
  animation: none !important;
}

/* 内容区域样式 */
.content-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
  max-height: none;
  height: calc(100% - 120px);
  width: 100%;
}

.content-wrapper::-webkit-scrollbar {
  display: none;
}

/* 历史对话加载占位样式 */
.history-loading {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #e0e0e0;
  border-top: 3px solid #3b6ce3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 底部区域样式 */
.bottom-area {
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  flex-shrink: 0;
  background-color: #eef3ff;
  border-top: 1px solid #fff;
  padding-bottom: 20px;
  box-shadow: 0px -10px 25.6px -2px #3b6ce326;
  padding-bottom: calc(env(safe-area-inset-bottom) + 30px); /* 适配iOS安全区+按钮高度 */
}

/* 输入区域样式 */
.input-area {
  padding: 12px;
  box-sizing: border-box;
  background-color: #eef3ff;
  border-top: 1px solid #ffffff;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

/* 输入区域样式调整 */
.input-wrapper {
  flex: 1;
  position: relative !important;
  height: auto;
  max-height: 150px;
  border-radius: 4px;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
}

.query-input {
  flex: 1;
  height: auto;
  background-color: #fff !important;
  max-height: 150px;
  padding: 8px 30px 8px 12px;
  border: none;
  font-size: 14px;
  resize: none;
  outline: none;
  font-family: inherit;
  background-color: transparent;
  overflow-y: auto;
  word-wrap: break-word;
  box-sizing: border-box; /* 关键：确保内边距包含在高度计算中 */
  color: #333;
  line-height: 1.4; /* 明确行高，避免内容撑开高度 */
}

/* 修复占位符导致的高度异常 */
.query-input::placeholder {
  line-height: normal;
}

/* .query-send-btn {
  position: absolute;
  right: 1px;
  top: 1px;
  width: 30px;
  height: 30px;
  border-radius: 5px;
  background-color: #3b6ce3;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
} */
/* 仅修改.query-send-btn 样式 */
.query-send-btn {
  position: absolute;
  right: 3px;
  top: 3px;
  width: 30px; /* 移动端放大按钮，适配触摸 */
  height: 30px;
  border-radius: 5px;
  background-color: #3b6ce3;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-top: 0;
}

/* 1. 移动端（触摸设备）适配：放大按钮+优化位置，避免误触 */
@media (hover: none) and (pointer: coarse) {
  .query-send-btn {
    top: 1px; /* 微调顶部间距，更贴合移动端视觉 */
  }
}

/* 确保输入框高度变化时容器同步调整 */
.query-input:focus {
  outline: none;
}

/* 确保输入框容器与输入框高度完全一致 */
.input-wrapper::after {
  content: '';
  display: table;
  clear: both;
}

.query-send-btn:disabled {
  background-color: #3b6ce380;
  cursor: not-allowed;
}

.query-send-btn img {
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1);
}

.query-send-btn:disabled img {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 筛选条件样式 */
.filter-bar {
  padding: 0 12px 0 12px;
  box-sizing: border-box;
}

.filter-content {
  position: relative;
}

.filter-headers-container {
  display: flex;
  gap: 0.9rem;
  overflow-x: auto;
  scrollbar-width: none;
  padding-bottom: 0.5rem;
}

.filter-headers-container::-webkit-scrollbar {
  display: none;
}

.filter-header-item {
  flex: 0 0 auto;
}

.filter-header {
  /*width: 81px;*/
  width: auto;
  max-width: 90px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem;
  background-color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  box-sizing: border-box;
}

.filter-header.active span {
  color: #3b6ce3;
}

.filter-header:active span {
  color: #3b6ce3;
}

.filter-icon {
  width: 12px;
  height: 8px;
  transition: transform 0.3s;
}

.filter-options-container {
  width: 100%;
  display: none;
  box-sizing: border-box;
}

.filter-options-container.show {
  display: block;
}

.filter-options-content {
  display: flex;
  gap: 0.8rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  scrollbar-width: none;
}

.filter-options-content::-webkit-scrollbar {
  display: none;
}

.option-btn {
  white-space: nowrap;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  background-color: #081e5566;
  color: #fff;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
}

.option-btn.active {
  background-color: #3b6ce3;
}
</style>
